import React, { useState, useEffect } from 'react';
import { MessageCircle, MapPin, Calendar, Sparkles, Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import { CONTACT_INFO } from '../data/services';
import { HeroVoucherTicket } from './HeroVoucherTicket';

interface HeroProps {
  onOpenBookingModal?: () => void;
  onNavigateToServices?: () => void;
}

interface PersonaItem {
  id: string;
  label: string;
  shortLabel: string;
  badge: string;
  tagline: string;
  perk: string;
  waMessage: string;
  image: string;
}

// Persona photo data
const PERSONAS: PersonaItem[] = [
  {
    id: 'working',
    label: 'Working Women',
    shortLabel: 'Working Women',
    badge: 'Desk-to-Glow',
    tagline: 'Zero parlour commute',
    perk: 'Early morning & late evening slots · Zero parlour rush',
    waMessage: 'Hi Blishka! I am a working professional and would like to book a convenient home beauty slot.',
    image: '/persona-working-woman.jpg'
  },
  {
    id: 'student',
    label: 'Students',
    shortLabel: 'Students',
    badge: 'Campus Ready',
    tagline: 'Pocket-friendly from ₹30',
    perk: 'Budget student combos · Gentle threading & glow cleanup',
    waMessage: 'Hi Blishka! I am a student and would like to book a budget-friendly home beauty service.',
    image: '/persona-student.jpg'
  },
  {
    id: 'housewife',
    label: 'Housewives',
    shortLabel: 'Housewives',
    badge: 'Home Comfort',
    tagline: '100% private at home',
    perk: 'Relax in your own living room while kids are in school',
    waMessage: 'Hi Blishka! I would like to book a relaxing home parlour appointment for myself.',
    image: '/persona-housewife.jpg'
  }
];

export const Hero: React.FC<HeroProps> = ({ onNavigateToServices }) => {
  const [activePersonaId, setActivePersonaId] = useState<string>('working');
  const [mobileSlideIndex, setMobileSlideIndex] = useState<number>(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  const handleBookNow = () => {
    if (onNavigateToServices) {
      onNavigateToServices();
    } else {
      const menuEl = document.getElementById('menu');
      if (menuEl) {
        menuEl.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const activePersona = PERSONAS.find(p => p.id === activePersonaId) || PERSONAS[0];
  const currentMobilePersona = PERSONAS[mobileSlideIndex];

  // Auto-scroll mobile banner every 4s
  useEffect(() => {
    const timer = setInterval(() => {
      setMobileSlideIndex(prev => (prev + 1) % PERSONAS.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  // Handle touch swipe for mobile slider
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX === null) return;
    const diff = touchStartX - e.changedTouches[0].clientX;
    if (diff > 40) {
      setMobileSlideIndex(prev => (prev + 1) % PERSONAS.length);
    } else if (diff < -40) {
      setMobileSlideIndex(prev => (prev - 1 + PERSONAS.length) % PERSONAS.length);
    }
    setTouchStartX(null);
  };

  return (
    <section className="hero-section">
      {/* Semantic H1 for search engines and assistive technologies */}
      <h1 className="sr-only">Blishka - Doorstep Salon &amp; Beauty Parlour Services in Bhilai and Durg</h1>

      <div className="section-container">
        
        {/* ── 1. MOBILE APP-STYLE BANNER SLIDER (Clean, Clear, Minimal) ── */}
        <div
          className="mobile-hero-slider"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          aria-label="Mobile promotional banner carousel"
        >
          {/* Clean Split Banner Card (Never cuts photo, minimal text) */}
          <div className="mobile-slide-card">
            {/* Left: Minimal Text Content */}
            <div className="mobile-banner-left">
              <span className="mobile-banner-pill">
                <Sparkles size={10} />
                <span>{currentMobilePersona.label}</span>
              </span>

              <h2 className="mobile-banner-title">{currentMobilePersona.badge}</h2>
              <p className="mobile-banner-tag">{currentMobilePersona.tagline}</p>

              <button
                type="button"
                onClick={handleBookNow}
                className="mobile-banner-book-btn"
                aria-label="Explore and book services"
              >
                <span>Book Now</span>
                <ChevronRight size={13} />
              </button>
            </div>

            {/* Right: Uncut, Crystal-Clear Photo */}
            <div className="mobile-banner-right">
              <img
                src={currentMobilePersona.image}
                alt={`Doorstep parlour service for ${currentMobilePersona.label} in Bhilai & Durg`}
                className="mobile-banner-photo"
                loading="eager"
              />
            </div>

            {/* Subtle Prev / Next Navigation Arrows */}
            <button
              type="button"
              className="mobile-slider-nav-btn prev"
              onClick={() => setMobileSlideIndex(prev => (prev - 1 + PERSONAS.length) % PERSONAS.length)}
              aria-label="Previous banner"
            >
              <ChevronLeft size={15} />
            </button>
            <button
              type="button"
              className="mobile-slider-nav-btn next"
              onClick={() => setMobileSlideIndex(prev => (prev + 1) % PERSONAS.length)}
              aria-label="Next banner"
            >
              <ChevronRight size={15} />
            </button>
          </div>

          {/* Slide Indicator Dots */}
          <div className="mobile-slider-dots">
            {PERSONAS.map((p, idx) => (
              <button
                key={p.id}
                type="button"
                className={`mobile-dot ${idx === mobileSlideIndex ? 'active' : ''}`}
                onClick={() => setMobileSlideIndex(idx)}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>

        {/* ── 2. DESKTOP HERO VIEW (Active on > 768px) ── */}
        <div className="desktop-hero-view">
          <div className="hero-layout">

            {/* Left Column */}
            <div>
              <div className="hero-brush-tag">
                Doorstep Beauty · Bhilai &amp; Durg
              </div>

              <h2 className="hero-main-title">
                Beauty, Now at Your Doorstep!
              </h2>

              <span className="hero-script-tagline">
                Your time · Your space · Our service &hearts;
              </span>

              <p className="hero-desc">
                Salon-quality pampering right in your living room — no rush, no queues. Verified female specialists, single-use kits, starting at just ₹30.
              </p>

              <div className="hero-location-badge">
                <MapPin size={14} color="var(--blishka-berry)" />
                <span>Serving all sectors across <strong>{CONTACT_INFO.serviceAreas}</strong></span>
              </div>

              <div className="hero-cta-group">
                <a
                  href={`https://wa.me/${CONTACT_INFO.whatsapp}?text=Hi%20Blishka!%20I%20would%20like%20to%20book%20a%20home%20beauty%20appointment%20in%20Bhilai/Durg.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-whatsapp"
                >
                  <MessageCircle size={17} />
                  <span>Book on WhatsApp</span>
                </a>

                <button onClick={handleBookNow} className="btn btn-berry" aria-label="Explore and pick services">
                  <Calendar size={16} />
                  <span>Pick Services</span>
                </button>

                <a
                  href="/services"
                  onClick={(e) => {
                    e.preventDefault();
                    if (onNavigateToServices) {
                      onNavigateToServices();
                    }
                  }}
                  className="btn btn-outline"
                >
                  View Services
                </a>
              </div>
            </div>

            {/* Right Column — persona photo cards + social proof */}
            <div>
              <div className="hero-audience-card">
                <div className="audience-header-banner">
                  <span className="audience-doodle-left" aria-hidden="true">🌿</span>
                  <div>
                    <span className="audience-hook-title">Busy Life?</span>
                    <div className="audience-hook-sub">WE GET IT!</div>
                    <p className="audience-hook-text">
                      Beauty that fits your schedule, not the other way around &hearts;
                    </p>
                  </div>
                  <span className="audience-doodle-right" aria-hidden="true">♥</span>
                </div>

                {/* Persona Photo Gallery Frames */}
                <div className="persona-gallery-grid">
                  {PERSONAS.map((persona) => {
                    const isActive = persona.id === activePersonaId;
                    return (
                      <div
                        key={persona.id}
                        className={`persona-frame-card ${isActive ? 'is-active' : ''}`}
                        onClick={() => setActivePersonaId(persona.id)}
                        onMouseEnter={() => setActivePersonaId(persona.id)}
                        role="button"
                        tabIndex={0}
                        aria-label={persona.label}
                      >
                        {/* Photo Container with Shimmer Effect */}
                        <div className="persona-frame-photo-wrap">
                          <img
                            src={persona.image}
                            alt={`Doorstep beauty specialist for ${persona.label} in Bhilai & Durg`}
                            loading="lazy"
                            className="persona-frame-img"
                          />
                          <div className="persona-shimmer-sweep" />

                          {/* Top decorative heart accent */}
                          <div className="persona-heart-pin">
                            <Heart size={12} fill="#e07a8b" color="#e07a8b" />
                          </div>

                          {/* Bottom Frosted Glass Pill */}
                          <div className="persona-glass-pill">
                            <span className="persona-pill-title">{persona.shortLabel}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Interactive Tailored Perk Box */}
                <div className="persona-active-perk-box">
                  <div className="persona-perk-header">
                    <span className="persona-perk-badge">
                      <Sparkles size={11} />
                      <span>{activePersona.badge}</span>
                    </span>
                    <span className="persona-perk-for">{activePersona.label}</span>
                  </div>
                  <p className="persona-perk-desc">{activePersona.perk}</p>
                  <div className="persona-perk-actions">
                    <a
                      href={`https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(activePersona.waMessage)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="persona-book-link"
                    >
                      <span>Book for {activePersona.shortLabel}</span>
                      <span className="persona-book-arrow">&rarr;</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* ── 3. CATCHY HERO VOUCHER TICKET (First Booking Slabs) ── */}
        <HeroVoucherTicket onClaim={handleBookNow} />

      </div>
    </section>
  );
};
